/* eslint-disable arrow-body-style */
import { faTimes } from "@fortawesome/pro-light-svg-icons";
import { faAngleDown } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import humanize from "lodash-es/startCase";
import hotkeys from "hotkeys-js";

import { getFeedback } from "extensions/sentry";
import { emitter } from "common/emitter";
import { store } from "common/store";
import { validateEmail } from "common/utils";

import { STOP_MIMIC } from "common/reducers/authentication";

import { CurrencyPicker } from "common/components/currencyPicker";
import { pazienHistory } from "common/components/pazienHistory";
import { CancelButton } from "common/components/buttons";

import { BusinessUnitPicker } from "merchant/features/businessUnitPicker/reactBUPicker";

import { IconBar } from "./iconBar";
import { PackageInfo } from "./packageInfo";
import { CreateBuDropdown } from "./create-bu-dropdown";

export function TopBar(props) {
    const user = useSelector((state) => state.common.user);
    const merchant = useSelector((state) => state.common.merchant_config);
    const isMimicking = useSelector((state) => Boolean(state.authentication.mimic_as_user));
    const hasAlerts2 = useSelector((state) => state.user_merchant_settings.alerts_version === 2 && state.permissions.user_permissions.feature_alerts);
    const hasBUManager = useSelector((state) => state.permissions.user_permissions.has_bu_manager);
    const canSeeDataStatus = useSelector((state) => state.permissions.user_permissions.can_view_data_availability);
    const hasMultipleMerchants = useSelector((state) => state.common.all_merchants.length > 1);
    const hasUserManagement = useSelector((state) => state.permissions.user_permissions.can_view_users);
    const dispatch = useDispatch();

    const hasBUs = Boolean(merchant?.business_units?.length);
    
    const buPickerRef = createRef();
    
    const showMerchantPicker = (e = false) => {
        if (e) e.preventDefault();
        emitter.emit("switchMerchant");
    };
    
    const stopMimic = (e) => {
        e.preventDefault();
        const navigateAfter = store.getState().authentication.mimic_original_path || "/";
        dispatch(STOP_MIMIC());
        window.location.href = navigateAfter;
    };
    
    const logout = (e) => {
        e.preventDefault();
        pazienHistory.navigate("logout", {
            trigger: true
        });
    };
    
    const goToPage = (e) => {
        e.preventDefault();
        pazienHistory.navigateWithParams(e.currentTarget.dataset.fragment, { trigger: true });
    };

    const getDisplayName = () => {
        const { username, first_name } = user;
        
        let displayName;
        
        // If user has a first name then use it
        if (first_name) {
            displayName = first_name;
        } else if (validateEmail(username)) {
            try {
                [displayName] = username.split("@");
            } catch {
                // Noop
            }
        } else {
            displayName = "Pazien user";
        }
        
        return humanize(displayName);
    };

    useEffect(() => {
        hotkeys("alt+m", (event, handler) => {
            // Prevent the default refresh event under WINDOWS system
            event.preventDefault();
            showMerchantPicker();
        });

        hotkeys("alt+shift+r", (event, handler) => {
            // Prevent the default refresh event under WINDOWS system
            event.preventDefault();
            getFeedback();
        });
    }, []);

    return (
        <>
            <div className="navbar-header">
                <button type="button" className="navbar-toggle collapsed" data-toggle="collapse" data-target="#topbar-navi">
                    <span className="sr-only">Toggle navigation</span>
                    <span className="icon-bar" />
                    <span className="icon-bar" />
                    <span className="icon-bar" />
                </button>
                <div className="merchant-nav2" translate="no">
                    <div className="parent-unit">
                        <div className="merchant-name">{pazienHistory.isAdminApp() ? "Administrator" : merchant.display_name || ""}</div>
                    </div>
                    {!pazienHistory.isAdminApp() && (
                        <div id="businessUnitSelector">
                            {hasBUs && <BusinessUnitPicker ref={buPickerRef} />}
                            {!hasBUs && hasBUManager && <CreateBuDropdown ref={buPickerRef} />}
                        </div>
                    )}
                </div>
            </div>

            <div className="collapse navbar-collapse" id="topbar-navi">
                <ul className="utility-nav nav navbar-nav navbar-right">
                    <li className="package-info">
                        <div className="hud">
                            <PackageInfo
                                trialEndDate={merchant.trial_end_date}
                                merchantID={merchant._id}
                                merchantName={merchant.display_name}
                                packageRef={merchant.package_ref}
                            />
                        </div>
                        
                    </li>
                    <li className="icon-menu"><IconBar /></li>
                    <li className="currency-selector"><CurrencyPicker /></li>
                    <li className="user-nav dropdown clearfix">
                        <a href="#" className="dropdown-toggle" data-toggle="dropdown" role="button" aria-expanded="false">
                            <span className="greeting">Hi, <span className="username">{getDisplayName()}</span> &nbsp;<FontAwesomeIcon icon={faAngleDown} /></span>
                        </a>
                        <ul className="dropdown-menu" role="menu">
                            {pazienHistory.isAdminApp()
                                ? (
                                    <>
                                        <li className="sign-out">
                                            <div className="sign-out-inner">
                                                {isMimicking && <CancelButton onClick={stopMimic}>Stop mimicking</CancelButton>}
                                                <CancelButton onClick={logout}>Sign out</CancelButton>
                                            </div>
                                        </li>
                                        <li className="extra-feature">
                                            <a target="_blank" rel="noopener noreferrer" href="/merchant">Merchant portal</a>
                                        </li>
                                    </>
                                )
                                : (
                                    <>
                                        <li>
                                            <span className="header">Your account</span>
                                            <span className="close"><FontAwesomeIcon icon={faTimes} /></span>
                                        </li>
                                        <li className="section-header">User settings</li>
                                        <li>
                                            <a href="#" data-fragment="preferences/profile" className="profile" onClick={goToPage}>Profile</a>
                                        </li>
                                        <li>
                                            <a href="#" data-fragment="preferences/preferences" className="profile" onClick={goToPage}>Preferences</a>
                                        </li>
                                        {user.sso_user === false && <li><a href="#" data-fragment="preferences/security" className="sign-in" onClick={goToPage}>Change password</a></li>}
                                        {hasAlerts2 && <li><a href="#" data-fragment="alerts2/inbox" className="alerts2" onClick={goToPage}>Alerts</a></li>}
                                        {hasUserManagement && (
                                            <>
                                                <li className="section-header">Admin tools</li>
                                                <li><a href="#" data-fragment="preferences/users" className="users" onClick={goToPage}>User management</a></li>
                                            </>
                                        )}
                                        {hasBUManager && <li><a href="#" data-fragment="preferences/bumanager" className="data" onClick={goToPage}>Business unit manager</a></li>}
                                        {canSeeDataStatus && <li><a href="#" data-fragment="preferences/data" className="data" onClick={goToPage}>Data status</a></li>}
                                        <li className="sign-out">
                                            <div className="sign-out-inner">
                                                {isMimicking && <CancelButton onClick={stopMimic}>Stop mimicking</CancelButton>}
                                                <CancelButton onClick={logout}>Sign out</CancelButton>
                                            </div>
                                        </li>
                                        {hasMultipleMerchants && <li className="extra-feature"> <a href="#" onClick={showMerchantPicker}>Switch merchant<div className="key-combo">Alt+M</div></a></li>}
                                        {user.superuser && <li className="extra-feature"><a target="_blank" rel="noopener noreferrer" href="/merchant/administrator">Pazien admin portal</a></li>}
                                    </>
                                )}
                        </ul>
                    </li>
                </ul>
            </div>
        </>
    );
}
